import {DOT, DASH, VARIABLE} from 'effects/midi/tidal/types.js';
import {dot, dash} from 'effects/midi/tidal/parser.js';
import {
    otherwise,
    REST,
    after,
    when,
    before,
    REGULAR,
    PIANO,
    LEGATO,
    STACCATO,
    FLAM,
    DOUBLE,
    PIANISSIMO,
    ACCENT} from 'effects/midi/scripting/trules.js';

let MAIN_TYPES = [DASH, DOT];
let TYPES_ORDER = [DASH, DOT, ACCENT, LEGATO, STACCATO, PIANO, PIANISSIMO, REGULAR, DOUBLE, FLAM, REST];

const NOTHING = {type: "NOTHING"};

const getType = (type) => {
    if (type.type) {
        return type.type.type || type.type;
    }
    return type;
};
   
export const getBlocks = (cells, size) => {
    let blocks = [];
    
    const visited = (x,y) => 
          blocks.some(
              block =>
                  block.some(b => b.x === x && b.y === y));

   
    const getBlockAt = (x,y) => {
        let block = [];

        for (let scanX=x; scanX < size; scanX++) {
            if (cells[y][scanX]) {
                block.push({x: scanX, y: y});
            } else {
                break;
            }

            if (y >= size - 1) {
                continue;
            }

            let line = scanLine(scanX, y+1);
            let lines = [line];
            let linesToProcess = [line];

            if (cells[y][scanX] === DASH) {
                let line = scanLine(scanX+1, y+1);
                lines.push(line);
                linesToProcess.push(line);
            }

            while (linesToProcess.length > 0) {
                let _line = linesToProcess.pop();
                if (_line.length === 0) {
                    continue;
                }
                _line.forEach(
                    pt => {
                        let line = scanLine(pt.x, _line[0].y+1);
                        if (line.length > 0) {
                            lines.push(line);
                            linesToProcess.push(line);
                        }

                        if (cells[pt.x, _line[0].y] === DASH) {
                            let line = scanLine(pt.x+1, _line[0].y+1);
                            if (line.length > 0) {
                                lines.push(line);
                                linesToProcess.push(line);
                            }
                        }

                    });
            }
            lines.forEach(
                line => block = [... block, ...line]);

            if (cells[y][scanX] === DASH) {
                scanX++;
            }
        }

        let _block = [];
        block.forEach(
            piece => {
                if (!_block.some(_piece =>
                                 _piece.x == piece.x && _piece.y === piece.y)) {
                    _block.push(piece);
                }
            });
        return _block;
    };
    
    const scanLine = (x,y, includeEmpties) => {
        if (y >= size) {
            return [];
        }
        let line = [];
        let scan = (_x) => {
            let cell = cells[y][_x];
            if (cell) {
                line.push({x: _x, y});
                return true;
            } else {
                return false;
            }
        };
        for (let _x=x; _x < size; _x++) {
            if (!scan(_x)) {
                break;
            }
            if (cells[y][_x] === DASH) {
                _x++;
            }
        }
        for (let _x=x-1; _x <= 0; _x--) {
            if (!scan(_x) && cells[y][_x-1] !== DASH) {
                break;
            }
        }
        return line;
    };

        for (let y=0; y < size; y++) {
            for (let x=0; x < size; x++) {
                let cell = cells[y][x];
                if (!cell || visited(x,y)) {
                    continue;
                }

                let block = getBlockAt(x,y);
                blocks.push(block);
            }
        }
        return blocks;
};


export const calculateRules = (cells, size) => {
   
    const processBlock = (block, every, offset) => {
        // first fill out dashes
        let hasDash = block.some(({x,y}) => cells[y][x] === DASH);
        let hasDot = block.some(({x,y}) => cells[y][x] === DOT);
        block = fillDotDashes(block, cells);
        block.sort((a,b) => a.x - b.x);

        let rules = [];

        const createRule = (predicate, type, pieces) => {
            return predicate(type)
                .every(every, offset)
                .always(
                    flattenSingletons(extractTypes(pieces)));
        };

        if (hasDash) {
            let _dash = withType(block, cells, DASH)[0];
            let beforeDash = withX(block, _dash.x - 1);
            if (beforeDash.length > 0) {
                rules.push(createRule(before, dash(0), beforeDash));
            }
            let whenDash0 = withX(block, _dash.x).filter(
                piece => getType(piece) !== DASH);
            if (whenDash0.length > 0) {
                rules.push(createRule(when, dash(0), whenDash0));
            }

            let whenDash1 = withX(block, _dash.x+1).filter(
                piece => getType(piece) !== DASH);
            if (whenDash1.length > 0) {
                rules.push(
                    createRule(when, dash(1), whenDash1)
                );
            }

            // the rest gets put in a delay-style series
            let afterPieces = block.filter(({x}) => x > _dash.x+1);

            if (afterPieces.length > 0) {
                let afterGroup = groupByX(afterPieces)
                    .map(extractTypes)
                    .map(flattenSingletons);
                
                rules.push(
                    after(dash(1))
                        .every(every, offset)
                        .series(... afterGroup));
            }
        } else {

            let type = getDominantBlockType(block, cells, false);
            let centerPiece = withType(block, cells, type)[0];
            if (!centerPiece) {
                return [];
            }
            let beforePieces = withX(block, centerPiece.x - 1);
            let whenPieces = withX(block, centerPiece.x).filter(
                piece => getType(piece) !== type);
            let afterPieces = withX(block, centerPiece.x + 1);

            let _type = type === DOT ? dot() : type;

            if (beforePieces.length > 0) {
                rules.push(createRule(
                    before, _type, beforePieces));
            }

            if (whenPieces.length > 0) {
                rules.push(createRule(
                    when, _type, whenPieces));
            }

            if (afterPieces.length > 0) {
                let afterGroup = groupByX(afterPieces)
                    .map(extractTypes)
                    .map(flattenSingletons);
                
                rules.push(
                    after(_type)
                        .every(every, offset)
                        .series(... afterGroup));
            }
        }
        rules.forEach(
            rule => rule.block = block);
        return rules;
    };

    let blocks = getBlocks(cells, size);
    let rules = [];
    let blocksByType = byType(blocks, cells);
    let typesSoFar = {};
    for (let block of blocks) {
        let type = getDominantBlockType(block, cells);
        let soFar = typesSoFar[type] || 0;
        rules = [
            ... rules,
            ... processBlock(block, blocksByType[type], soFar)
        ];
        typesSoFar[type] = soFar+1;
    }

    rules.push(otherwise().always(REST));

    return rules;
};

const flattenSingletons = (l) => l.length === 1 ? l[0] : l;

const groupByX = (pieces) => {
    let list = [];
    pieces.forEach(
        piece => {
            if (list.length === 0) {
                list.push([piece]);
            } else {
                let last = list[list.length - 1];
                if (last[0].x === piece.x) {
                    last.push(piece);
                } else {
                    list.push([piece]);
                }
            }
        });
    return list;
};

const extractTypes = (pieces) => pieces.map(p => p.type);

export const withType = (block, cells, type) => {
    return block.filter(({x,y}) => cells[y][x] === type);
};

export const withX = (block, x) => {
    return block.filter(b => b.x === x);
};


const fillDotDashes = (block, cells) => {
    let _block = [];
    for (let {x,y} of block) {
        let cell = cells[y][x];
        if (cell  === DASH) {
            _block.push({
                x, y, type: dash(0)
            });
            _block.push({
                x: x+1, y, type: dash(1)
            });
        } else if (cell === DOT) {
            _block.push({
                x, y, type: dot()
            });
        } else {
            _block.push({x,y, type: cell});
        }
    }
    return _block;
};

export const getDominantBlockType = (block, cells, convert=true) => {
    for (let type of TYPES_ORDER) {
        if (block.some(p => cells[p.y][p.x] === type)) {
            return convert ? getType(type) : type;
        }
    }
    return undefined;
};

const byType = (blocks, cell) => {
    let mapping = {};
    blocks.forEach(
        block => {
            let type = getDominantBlockType(block, cell);
            if (!mapping[type]) {
                mapping[type] = 0;
            }
            mapping[type]++;
        });
    return mapping;
};
